import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Grid, Paper, Skeleton, Stack, Typography, alpha, useTheme } from '@mui/material';
import { Block, FileDownloadOutlined, PersonSearchOutlined } from '@mui/icons-material';
import SearchableSelect from '../../components/common/SearchableSelect';
import FilterBar from '../../components/common/FilterBar';
import EmptyState from '../../components/common/EmptyState';
import BusyIcon from '../../components/common/BusyIcon';
import PersonAvatar from '../../components/common/PersonAvatar';
import AcademicCard from '../../components/analytics/report/AcademicCard';
import AttendanceCard from '../../components/analytics/report/AttendanceCard';
import LettersCard from '../../components/analytics/report/LettersCard';
import StudentReportDocument from '../../components/analytics/pdf/StudentReportDocument';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useAppSelector } from '../../store/hooks';
import { useSessionContext } from '../../context/SessionContext';
import { useAuth } from '../../context/AuthContext';
import { getGradeColorFromPercentage } from '../../utils/gradeUtils';
import { attendanceRateColor } from '../../utils/attendanceStatus';
import { buildSessionOptions } from '../../utils/sessionOptions';
import { fmtClassName } from '../../utils/pdf/format';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import { getStudentsForAnalytics, getStudentReport, StudentsForAnalytics, StudentReportData } from '../../services/analyticsService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { Session, SessionClass, SectionInstance } from '../../types/sessions';

type StudentRow = StudentsForAnalytics['students'][number];

/** "Class 9 – Sci", from an enrolment. */
const placement = (e: StudentRow['enrollmentInfo']) =>
  (e ? `${fmtClassName(e.className)}${e.sectionName ? ` – ${e.sectionName}` : ''}` : 'Not enrolled');

/** A headline figure beside the student's name. */
const Figure: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box sx={{ px: 2.5, py: 1.25, minWidth: 110 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
    <Typography variant="h5" fontWeight={700} sx={{ color, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
);

/**
 * Reports → Students: one student's session — every exam and subject,
 * attendance, and letters and fines — with a PDF of the same. Pick the
 * session, narrow by class and section if you like, then the student.
 */
const StudentReports: React.FC = () => {
  const theme = useTheme();
  const { canRead } = useModulePermissions();
  const { activeSession } = useSessionContext();
  const grades = useAppSelector(state => state.grades.grades);
  const { currency } = useAuth();
  const { generate: generatePdf, loading: pdfLoading } = usePdfExport();
  const { branding } = useSchoolBranding();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionInstanceId, setSectionInstanceId] = useState('');
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [studentId, setStudentId] = useState('');
  const [report, setReport] = useState<StudentReportData | null>(null);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only the latest request may land — a quick switch never shows the previous student's report.
  const reportRequest = useRef(0);

  useEffect(() => {
    Promise.all([sessionsApi.list(), classesApi.list()])
      .then(([s, c]) => { setSessions(s); setClasses(c); })
      .catch((e: any) => setError(e?.message || 'Could not load the filters'));
  }, []);

  useEffect(() => { if (!sessionId && activeSession) setSessionId(activeSession._id); }, [activeSession, sessionId]);

  useEffect(() => {
    setSections([]);
    if (!sessionId || !classId) return;
    let live = true;
    sectionInstancesApi.list({ sessionId, classId }).then(s => { if (live) setSections(s); }).catch(() => { if (live) setSections([]); });
    return () => { live = false; };
  }, [sessionId, classId]);

  // The students the filters match; a chosen student who drops out of the list is cleared.
  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    setStudentsLoading(true);
    getStudentsForAnalytics({ sessionId, classId: classId || undefined, sectionInstanceId: sectionInstanceId || undefined, page: 1, limit: 1000 })
      .then(d => {
        if (!live) return;
        setStudents(d.students);
        setStudentId(id => (d.students.some(s => s._id === id) ? id : ''));
      })
      .catch((e: any) => { if (live) setError(e?.message || 'Could not load the students'); })
      .finally(() => { if (live) setStudentsLoading(false); });
    return () => { live = false; };
  }, [sessionId, classId, sectionInstanceId]);

  // The chosen student's report, for the session in view.
  useEffect(() => {
    const request = ++reportRequest.current;
    setReport(null);
    if (!studentId || !sessionId) { setReportLoading(false); return; }
    setReportLoading(true);
    setError(null);
    getStudentReport(studentId, { includeResults: true, includeLetters: true, sessionId, classId: classId || undefined, sectionInstanceId: sectionInstanceId || undefined })
      .then(r => { if (request === reportRequest.current) setReport(r); })
      .catch((e: any) => { if (request === reportRequest.current) setError(e?.response?.data?.message || e?.message || 'Could not load the report'); })
      .finally(() => { if (request === reportRequest.current) setReportLoading(false); });
  }, [studentId, sessionId, classId, sectionInstanceId]);

  const studentOptions = useMemo(() => students.map(s => ({
    value: s._id,
    label: s.name,
    // The enrolment's roll number — the student record's own can be stale.
    secondary: `Roll ${s.enrollmentInfo?.rollNumber ?? s.rollNumber} · ${placement(s.enrollmentInfo)}`,
  })), [students]);

  const downloadPdf = async () => {
    if (!report) return;
    const name = report.student.name.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
    await generatePdf(
      <StudentReportDocument report={report} grades={grades} currency={currency || 'PKR'} branding={branding} generatedAt={new Date().toISOString()} />,
      `${name}_student_report.pdf`,
    );
  };

  if (!canRead('student_reports')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <EmptyState icon={<Block />} title="No access" description="You don't have permission to view student reports. Ask your administrator for access." />
      </Box>
    );
  }

  const enrolment = report?.student.enrollmentInfo ?? null;
  const perf = report?.academicData.academicPerformance;
  const att = report?.administrativeData.attendanceOverview;
  const scored = (report?.academicData.resultsHistory ?? []).filter((r: any) => r.overallPercentage != null).length;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Student Reports</Typography>
          <Typography variant="body2" color="text.secondary">One student's session at a glance — results, attendance, and letters and fines.</Typography>
        </Box>
        {/* The same quiet outlined Download as the other report pages. */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<BusyIcon busy={pdfLoading}><FileDownloadOutlined fontSize="small" /></BusyIcon>}
          onClick={downloadPdf}
          disabled={!report || pdfLoading}
          sx={{
            textTransform: 'none', fontWeight: 500, borderColor: theme.palette.divider, color: theme.palette.text.secondary,
            '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04), color: theme.palette.primary.main },
          }}
        >
          {pdfLoading ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
      </Stack>

      <FilterBar loading={studentsLoading || reportLoading}>
        <SearchableSelect
          label="Session"
          value={sessionId}
          onChange={v => { setSessionId(v); setClassId(''); setSectionInstanceId(''); }}
          options={buildSessionOptions(sessions, theme)}
          clearable={false}
          minWidth={190}
        />
        <SearchableSelect
          label="Class"
          value={classId}
          onChange={v => { setClassId(v); setSectionInstanceId(''); }}
          options={classes.map(c => ({ value: c._id, label: fmtClassName(c.name) }))}
          placeholder="All classes"
          disabled={!sessionId}
          minWidth={160}
        />
        <SearchableSelect
          label="Section"
          value={sectionInstanceId}
          onChange={setSectionInstanceId}
          options={sections.map(s => ({ value: s._id, label: s.name }))}
          placeholder="All sections"
          disabled={!classId || sections.length === 0}
          minWidth={150}
        />
        <Box sx={{ flex: 1 }} />
        <SearchableSelect
          label="Student"
          value={studentId}
          onChange={setStudentId}
          options={studentOptions}
          placeholder={studentsLoading ? 'Loading students…' : `Choose a student${students.length ? ` (${students.length})` : ''}`}
          disabled={!sessionId || studentsLoading}
          loading={studentsLoading}
          minWidth={300}
          filterFn={(o, input) => `${o.label} ${o.secondary ?? ''}`.toLowerCase().includes(input.trim().toLowerCase())}
        />
      </FilterBar>

      {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!studentId ? (
        <Paper variant="outlined" sx={{ borderRadius: 2.5 }}>
          <EmptyState
            icon={<PersonSearchOutlined />}
            title="Choose a student"
            description={students.length || studentsLoading
              ? 'Pick the student from the list — narrow it by class and section if you like.'
              : 'No students are enrolled for these filters.'}
          />
        </Paper>
      ) : !report && !reportLoading ? (
        <Paper variant="outlined" sx={{ borderRadius: 2.5 }}>
          <EmptyState icon={<PersonSearchOutlined />} title="The report could not load" description="Try choosing the student again." />
        </Paper>
      ) : !report ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={96} sx={{ borderRadius: 2.5 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 2.5 }} /></Grid>
            <Grid size={{ xs: 12, md: 4 }}><Skeleton variant="rounded" height={420} sx={{ borderRadius: 2.5 }} /></Grid>
          </Grid>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, px: 2.5, py: 2, minWidth: 0 }}>
                <PersonAvatar name={report.student.name} size={52} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} noWrap>{report.student.name}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    Roll {enrolment?.rollNumber ?? report.student.rollNumber} · {placement(enrolment)}{enrolment?.sessionName ? ` · ${enrolment.sessionName}` : ''}
                  </Typography>
                  {report.student.fatherName && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      s/o {report.student.fatherName}{report.student.fatherPhone ? ` · ${report.student.fatherPhone}` : ''}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" sx={{ borderTop: { xs: `1px solid ${theme.palette.divider}`, md: 'none' }, borderLeft: { md: `1px solid ${theme.palette.divider}` }, '& > div + div': { borderLeft: `1px solid ${theme.palette.divider}` } }}>
                <Figure label="Overall" value={scored ? `${perf!.overallPercentage.toFixed(1)}%` : '—'} color={scored ? getGradeColorFromPercentage(perf!.overallPercentage, grades) : undefined} />
                <Figure label="Attendance" value={att!.totalDays ? `${att!.attendanceRate}%` : '—'} color={att!.totalDays ? attendanceRateColor(att!.attendanceRate) : undefined} />
                <Figure label="Exams" value={String(scored)} />
                <Figure label="Letters" value={String(report.administrativeData.lettersSummary.total)} />
              </Stack>
            </Stack>
          </Paper>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 8 }}><AcademicCard report={report} grades={grades} /></Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2}>
                <AttendanceCard report={report} />
                <LettersCard report={report} />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      )}
    </Box>
  );
};

export default StudentReports;
